import { createContext, useContext } from "react";
import type { ParameterGroup } from "../generated/api/types.gen";

/**
 * Page-scoped to /refine. Mounted by ParameterPanel to share form
 * status (dirty flags, disable state, group-expansion handlers) with
 * its descendant inputs. Do not mount at the application root.
 */
export interface ParameterFormContextValue {
  isDirty: boolean;
  promptDirty: boolean;
  /** Anything that makes the image refinable: a dirty parameter, a dirty
   *  prompt, or an attached reference image. Shared with the Enter hotkey so
   *  the button and the hotkey can't disagree. */
  hasChanges: boolean;
  isFormDisabled: boolean;
  isExpandDisabled: boolean;
  onParameterChange: () => void;
  onExpandGroup: (groupName: string) => void;
  expandingGroupName: string | null;
  isExpandingGroup: boolean;
  /** True while the backend re-reads auto parameter values from a freshly
   * rendered refinement image; eligible params show a syncing indicator. */
  isResyncingParameters: boolean;
  /** Project the refined image belongs to; null when refining outside one. */
  projectId?: string | null;
  /** Live form state, so a group can tell how many component replacements are
   *  already staged and stop offering a third. */
  formParameterGroups: ParameterGroup[];
}

const ParameterFormContext = createContext<ParameterFormContextValue | null>(
  null,
);

export function ParameterFormProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: ParameterFormContextValue;
}) {
  return (
    <ParameterFormContext.Provider value={value}>
      {children}
    </ParameterFormContext.Provider>
  );
}

export function useParameterForm(): ParameterFormContextValue {
  const ctx = useContext(ParameterFormContext);
  if (!ctx) {
    throw new Error(
      "useParameterForm must be used within ParameterFormProvider",
    );
  }
  return ctx;
}
